/**
 * Dim the Windows caption buttons while a modal scrim is up.
 *
 * The minimize/maximize/close buttons on Windows are not DOM: they are an
 * OS-composited overlay drawn ABOVE the page (`titleBarOverlay`, see
 * electron/windowChrome.ts). A dialog's `bg-black/45` scrim is a DOM element,
 * so it stops at their edge — with a dialog open they stayed at full
 * brightness on an otherwise dimmed window. The fix is to repaint the overlay
 * in the colours the scrim would have produced, which only the main process
 * can do; this hook is the renderer half of that round trip.
 *
 * It is mounted once, by `IntegratedTitlebar` — the component that already
 * owns the caption-button strip — and reads the scrim count the overlays
 * publish, so no dialog has to know the window's frame exists.
 *
 * macOS is deliberately not covered: its traffic lights belong to the system
 * frame and cannot be recoloured by the app at all.
 */

import { useEffect, useRef } from 'react';
import { useModalScrimStore } from '../store/modalScrimStore';
import { getWindowChrome } from '../../features/main-toolbar/components/IntegratedTitlebar';

/** Only Windows draws caption buttons over the page; everywhere else is a no-op. */
function hasOverlayCaptionButtons(): boolean {
  const chrome = getWindowChrome();
  return chrome.mode === 'integrated' && chrome.platform === 'win32';
}

export function useCaptionButtonScrim(): void {
  const isDimmed = useModalScrimStore((state) => state.openScrimCount > 0);
  // `null` until the first push: the main process cannot see the page's state,
  // so a renderer that reloaded (dev HMR, a crash recovery) while a dialog was
  // up would otherwise leave the caption buttons dim with nothing on screen to
  // explain it. Announcing the state once on mount re-syncs it either way.
  const lastPushed = useRef<boolean | null>(null);

  useEffect(() => {
    if (!hasOverlayCaptionButtons()) return;
    if (isDimmed === lastPushed.current) return;

    lastPushed.current = isDimmed;
    void window.electronAPI?.windowChrome?.setModalScrim?.(isDimmed);
  }, [isDimmed]);
}
